/**
 * ConfigurationCard - Card component for displaying configuration information
 *
 * Displays configuration name (editable), bond years, and millage schedule.
 * Supports inline name editing via edit icon and delete functionality.
 */

import { Badge, Box, HStack, Icon, IconButton, Input, Text, VStack } from '@chakra-ui/react';
import { ArrowRight, Check, Edit, Trash2, X } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { hasMillageOverrides, hasTaxableValueOverrides } from '~/services/override-service';
import type { Configuration } from '~/types/project';

interface ConfigurationCardProps {
  configuration: Configuration;
  isSelected: boolean;
  onClick: () => void;
  onNameEdit?: (newName: string) => void;
  onDelete?: () => void;
}

export const ConfigurationCard: React.FC<ConfigurationCardProps> = ({
  configuration,
  isSelected,
  onClick,
  onNameEdit,
  onDelete,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  // Name is set at creation time; fallback for legacy data only
  const displayName = configuration.name || `Config ${configuration.orderId || 1}`;

  // Get unique tax years from bond payment maps (the actual tax years bonds are associated with)
  const allTaxYears = new Set<number>();
  for (const bond of configuration.bondSeries) {
    // Get the first tax year from the bond's payment map
    const paymentEntries = Object.values(bond.bondPaymentMap);
    if (paymentEntries.length > 0) {
      // Use the first payment's tax year as the bond's associated year
      allTaxYears.add(paymentEntries[0].taxYear);
    }
  }
  const bondYears = [...allTaxYears].sort();

  // Get millage values and durations directly
  const peakMillage = configuration.millageInputs.peakMillage;
  const peakYears = configuration.millageInputs.peakMillageDuration;
  const minMillage = configuration.millageInputs.minimumMillage;
  const minYears = configuration.millageInputs.minimumMillageDuration;

  // Check for overrides
  const hasTVOverrides = hasTaxableValueOverrides(configuration.overrides);
  const hasMilOverrides = hasMillageOverrides(configuration.overrides);

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click when clicking edit icon
    setEditValue(displayName);
    setIsEditing(true);
  };

  const handleSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (editValue.trim() && onNameEdit) {
      onNameEdit(editValue.trim());
    }
    setIsEditing(false);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(false);
    setEditValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      if (editValue.trim() && onNameEdit) {
        onNameEdit(editValue.trim());
      }
      setIsEditing(false);
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      setIsEditing(false);
      setEditValue('');
    }
  };

  return (
    <Box
      minWidth="200px"
      borderWidth="1px"
      borderRadius="md"
      p={4}
      cursor="pointer"
      onClick={onClick}
      transform={isSelected ? 'scale(1.05)' : 'scale(1)'}
      boxShadow={isSelected ? 'md' : 'sm'}
      borderColor={isSelected ? 'blue.500' : 'gray.200'}
      bg={isSelected ? 'blue.50' : 'white'}
      transition="all 0.3s ease"
      position="relative"
    >
      <VStack align="stretch" gap={2}>
        {/* Row 1: Title only */}
        {isEditing ? (
          <HStack gap={1} minWidth={0}>
            <Input
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              fontSize="md"
              fontWeight="semibold"
              size="sm"
              autoFocus
              flex={1}
              minWidth={0}
              onClick={(e) => e.stopPropagation()}
            />
            <IconButton
              aria-label="Save name"
              size="xs"
              variant="ghost"
              colorPalette="green"
              onClick={handleSave}
              flexShrink={0}
            >
              <Icon>
                <Check size={12} />
              </Icon>
            </IconButton>
            <IconButton
              aria-label="Cancel editing"
              size="xs"
              variant="ghost"
              onClick={handleCancel}
              flexShrink={0}
            >
              <Icon>
                <X size={12} />
              </Icon>
            </IconButton>
          </HStack>
        ) : (
          <Text
            fontSize="md"
            fontWeight="semibold"
            color={isSelected ? 'blue.700' : 'gray.800'}
            minWidth={0}
            lineClamp={1}
          >
            {displayName}
          </Text>
        )}

        {/* Row 2: Edit, TV badge (if present), Delete */}
        {!isEditing && (
          <HStack justify="space-between" align="center" gap={2}>
            {onNameEdit ? (
              <IconButton
                aria-label="Edit configuration name"
                size="xs"
                variant="ghost"
                onClick={handleEditClick}
                flexShrink={0}
              >
                <Icon>
                  <Edit size={12} />
                </Icon>
              </IconButton>
            ) : (
              <Box flexShrink={0} />
            )}
            {/* TV badge in the middle if present */}
            {hasTVOverrides ? (
              <Badge
                size="sm"
                colorPalette="yellow"
                variant="subtle"
                fontSize="xs"
                fontWeight="medium"
              >
                TV+
              </Badge>
            ) : (
              <Box flex={1} />
            )}
            {onDelete ? (
              <IconButton
                aria-label="Delete configuration"
                size="xs"
                variant="ghost"
                colorPalette="red"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                flexShrink={0}
              >
                <Icon>
                  <Trash2 size={12} />
                </Icon>
              </IconButton>
            ) : (
              <Box flexShrink={0} />
            )}
          </HStack>
        )}

        {/* Row 3: Bonds */}
        <Text fontSize="sm" color="gray.600">
          {bondYears.length <= 3 ? 'Bonds: ' : ''}
          {bondYears.join(', ')}
        </Text>

        {/* Row 4: Millage schedule: peak:years → min:years (or Mil+ badge replaces arrow) */}
        <HStack justify="space-between" gap={2} fontSize="sm">
          <Text fontWeight="semibold" color="gray.900">
            {peakMillage} : {peakYears}
          </Text>
          {hasMilOverrides ? (
            <Badge
              size="sm"
              colorPalette="purple"
              variant="subtle"
              fontSize="xs"
              fontWeight="medium"
            >
              Mil+
            </Badge>
          ) : (
            <Icon color="gray.600">
              <ArrowRight size={16} strokeWidth={3} />
            </Icon>
          )}
          <Text fontWeight="semibold" color="gray.900">
            {minMillage} : {minYears}
          </Text>
        </HStack>
      </VStack>
    </Box>
  );
};
